import type { ReactNode } from "react";
import type { Editor } from "@tiptap/react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Link2,
  Highlighter,
  Quote,
  List,
  ListOrdered,
  ListChecks,
  Heading1,
  Heading2,
  Heading3,
} from "lucide-react";

export type FormatAction = {
  id: string;
  label: string;
  icon: ReactNode;
  hint: string;
  /** Whether the editor should treat this shortcut as already-bound by the
   *  underlying Tiptap extension. If true, our React keydown handler skips
   *  calling apply() — letting Tiptap's own keymap fire — but we still match
   *  the key for ShortcutsMenu filtering / display. */
  builtin?: boolean;
  matchKey: (e: KeyboardEvent | React.KeyboardEvent) => boolean;
  apply: (editor: Editor) => void;
};

const ICON_SIZE = 14;
const mod = (e: KeyboardEvent | React.KeyboardEvent) => e.metaKey || e.ctrlKey;

/**
 * Tiptap chains the active editor command and `.run()` actually applies it.
 * `.focus()` makes sure the editor selection is the one acted on (relevant
 * when the user triggered the action from the shortcuts menu, which has
 * stolen focus).
 */
function promptLink(editor: Editor) {
  const previous = editor.getAttributes("link").href as string | undefined;
  const url = window.prompt("URL?", previous ?? "https://");
  if (url === null) return;
  if (url === "") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return;
  }
  editor
    .chain()
    .focus()
    .extendMarkRange("link")
    .setLink({ href: url })
    .run();
}

export const FORMAT_ACTIONS: FormatAction[] = [
  {
    id: "bold",
    label: "bold",
    hint: "⌘B",
    icon: <Bold size={ICON_SIZE} />,
    builtin: true,
    matchKey: (e) => mod(e) && !e.shiftKey && !e.altKey && e.code === "KeyB",
    apply: (editor) => editor.chain().focus().toggleBold().run(),
  },
  {
    id: "italic",
    label: "italic",
    hint: "⌘I",
    icon: <Italic size={ICON_SIZE} />,
    builtin: true,
    matchKey: (e) => mod(e) && !e.shiftKey && !e.altKey && e.code === "KeyI",
    apply: (editor) => editor.chain().focus().toggleItalic().run(),
  },
  {
    id: "underline",
    label: "underline",
    hint: "⌘U",
    icon: <Underline size={ICON_SIZE} />,
    builtin: true,
    matchKey: (e) => mod(e) && !e.shiftKey && !e.altKey && e.code === "KeyU",
    apply: (editor) => editor.chain().focus().toggleUnderline().run(),
  },
  {
    id: "strikethrough",
    label: "strikethrough",
    hint: "⌘⇧X",
    icon: <Strikethrough size={ICON_SIZE} />,
    matchKey: (e) => mod(e) && e.shiftKey && !e.altKey && e.code === "KeyX",
    apply: (editor) => editor.chain().focus().toggleStrike().run(),
  },
  {
    id: "code",
    label: "code",
    hint: "⌘E",
    icon: <Code size={ICON_SIZE} />,
    builtin: true,
    matchKey: (e) => mod(e) && !e.shiftKey && !e.altKey && e.code === "KeyE",
    apply: (editor) => editor.chain().focus().toggleCode().run(),
  },
  {
    id: "link",
    label: "link",
    hint: "⌘K",
    icon: <Link2 size={ICON_SIZE} />,
    matchKey: (e) => mod(e) && !e.shiftKey && !e.altKey && e.code === "KeyK",
    apply: (editor) => promptLink(editor),
  },
  {
    id: "highlight",
    label: "highlight",
    hint: "⌘⇧H",
    icon: <Highlighter size={ICON_SIZE} />,
    matchKey: (e) => mod(e) && e.shiftKey && !e.altKey && e.code === "KeyH",
    apply: (editor) => editor.chain().focus().toggleHighlight().run(),
  },
  {
    id: "quote",
    label: "blockquote",
    hint: "⌘⇧.",
    icon: <Quote size={ICON_SIZE} />,
    matchKey: (e) => mod(e) && e.shiftKey && !e.altKey && e.code === "Period",
    apply: (editor) => editor.chain().focus().toggleBlockquote().run(),
  },
  {
    id: "bullet",
    label: "bullet list",
    hint: "⌘⇧8",
    icon: <List size={ICON_SIZE} />,
    builtin: true,
    matchKey: (e) => mod(e) && e.shiftKey && !e.altKey && e.code === "Digit8",
    apply: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    id: "numbered",
    label: "numbered list",
    hint: "⌘⇧7",
    icon: <ListOrdered size={ICON_SIZE} />,
    builtin: true,
    matchKey: (e) => mod(e) && e.shiftKey && !e.altKey && e.code === "Digit7",
    apply: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    id: "checklist",
    label: "checklist",
    hint: "⌘⇧9",
    icon: <ListChecks size={ICON_SIZE} />,
    matchKey: (e) => mod(e) && e.shiftKey && !e.altKey && e.code === "Digit9",
    apply: (editor) => editor.chain().focus().toggleTaskList().run(),
  },
  {
    id: "h1",
    label: "heading 1",
    hint: "⌘⌥1",
    icon: <Heading1 size={ICON_SIZE} />,
    builtin: true,
    matchKey: (e) => mod(e) && !e.shiftKey && e.altKey && e.code === "Digit1",
    apply: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    id: "h2",
    label: "heading 2",
    hint: "⌘⌥2",
    icon: <Heading2 size={ICON_SIZE} />,
    builtin: true,
    matchKey: (e) => mod(e) && !e.shiftKey && e.altKey && e.code === "Digit2",
    apply: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    id: "h3",
    label: "heading 3",
    hint: "⌘⌥3",
    icon: <Heading3 size={ICON_SIZE} />,
    builtin: true,
    matchKey: (e) => mod(e) && !e.shiftKey && e.altKey && e.code === "Digit3",
    apply: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
];

// Cmd+/ opens the shortcuts menu.
export function isShortcutsMenuKey(e: KeyboardEvent | React.KeyboardEvent) {
  return mod(e) && !e.shiftKey && !e.altKey && e.code === "Slash";
}
